<template>

  <div class="card-base card-shadow--medium widget small-widget queue-widget">
    <div class="widget-value ph-10">
      <div class="flex mb-10 mt-10 align-center">
        <span class="mr-5 flex justify-center align-center">
          <v-svg width="18" height="18" class="voxo-yellow" sprite="call-inbound"/>
        </span>
        <el-input
          class="widget-name-input ml-10"
          v-model="widgetName"
        />
        <el-button
          @click="removeWidget"
          class="table-action-button ml-10"
        >
          <i class="el-icon-delete voxo-dark-red" />
        </el-button>
      </div>

      <h1 class="m-0 text-truncate voxo-blue text-center stat-number">
        {{ widgetValue }}
      </h1>
      <div class="flex justify-space-between mb-10">
        <div class="stat-breakdown"></div>
        <div class="stat-breakdown">
          <div class="text-right">
            <span> {{ longestCurrentHoldValue ? timeFormatter({cellValue: longestCurrentHoldValue}) : 'N/A' }}</span>
          </div>
          <h5 class="mt-0 mb-0"> LONGEST WAIT </h5>
        </div>
      </div>
    </div>

    <div class="stat-widget-table ph-10" v-show="isMultiQueue">
      <vxe-table
        round
        height="100%"
        border="inner"
        ref="xGrid"
        class="user-stats-table"
        empty-text="No Data"
        sync-resize
        auto-resize
        :checkbox-config="{checkAll: determineCheckAll()}"
        @checkbox-change="checkboxesChanged"
        @checkbox-all="checkboxesChanged"
        :show-overflow="true"
        :scroll-y="{enabled: true, gt: 10, oSize: 0}"
        :data="queueStoreData"
      >
        <vxe-table-column
          field="checkbox"
          type="checkbox"
          width="30"
        />
        <vxe-table-column
          field="queueId"
          title="Queue"
          min-width="200"
          sortable
          :formatter="getQueueName"
        />
        <vxe-table-column
          field="waitingCalls"
          title="Count"
          min-width="80"
          sortable
          sort-type="number"
        />
        <vxe-table-column
          field="longestCurrentHold"
          title="Longest Wait"
          min-width="150"
          sortable
          :sort-by="holdTimeSort"
          :formatter="timeFormatter"
        />
      </vxe-table>
    </div>

  </div>

</template>

<script>

import {mapGetters} from "vuex";
import {debounce} from 'lodash';

export default {
  name: 'ActiveCallsQueue',
  props: {
    widgetIndex: {required: true},
    title: {required: true},
    filterQueueIds: {required: true},
    queueNameKey: {required: true},
  },
  data() {
    return {
      widgetValue: 0,
      longestCurrentHoldValue: null,
      widgetName: this.title,
      filtersActive: false,
      filterQueues: this.filterQueueIds,
      viewStyle: 'default'
    }
  },
  computed: {

    ...mapGetters('queueDashQueues', {queueStoreData: 'list'}),

    waitingCalls() {
      return this.$store.state.queueManagerAggregates.waitingCalls;
    },

    longestCurrentHold() {
      return this.$store.state.queueManagerAggregates.longestCurrentHold;
    },

    isMultiQueue() {
      return this.queueStoreData.length > 1;
    }
  },
  watch: {
    waitingCalls: debounce(function () {
      this.widgetValue = this.filtersActive ? this.getFilteredValue() : this.waitingCalls
    }, 150),
    longestCurrentHold: debounce(function () {
      this.longestCurrentHoldValue = this.filtersActive ? this.getFilteredLongestHold() : this.longestCurrentHold;
    }, 150),
    widgetName: debounce(function() {
      if(this.widgetName === this.title) return;
      this.$emit('nameUpdate', {name: this.widgetName, index: this.widgetIndex});
    }, 750),
    filterQueues: debounce(function () {
      this.$emit('filterQueuesUpdate', {filterQueueIds: this.filterQueues, index: this.widgetIndex})
    }, 750)
  },
  methods: {

    checkboxesChanged({records}) {

      console.log('checkbox change');

      this.filterQueues = records.map(item => item.queueId);

      if(this.filterQueues.length === this.queueStoreData.length){
        this.widgetValue = this.waitingCalls;
        this.filtersActive = false;
        return;
      }

      this.widgetValue = this.getFilteredValue();
      this.filtersActive = true;
    },

    getQueueName(data) {
      const { cellValue } = data;
      return this.queueNameKey[cellValue] ? this.queueNameKey[cellValue] : 'Unknown';
    },

    removeWidget(){
      this.$emit('removeWidget', this.widgetIndex);
    },

    getFilteredValue() {

      if(!this.filterQueues.length) return 0;

      let value = 0;
      for (let item of this.queueStoreData) {
        if (this.filterQueues.includes(item.queueId)) {
          value += item.waitingCalls;
        }
      }

      return value;
    },

    getFilteredLongestHold() {
      if(!this.filterQueues.length) return null;

      let value = null;
      for(let item of this.queueStoreData) {
        if(this.filterQueues.includes(item.queueId)) {
          value = value === null ? item.longestCurrentHold : item.longestCurrentHold > value ? item.longestCurrentHold : value
        }
      }

      return value;
    },

    determineCheckAll() {

      const hasFilter = this.filterQueues.length !== this.queueStoreData.length;

      if(!hasFilter){
        this.getInitialValue();
        return true;
      }

      if(hasFilter){
        this.filtersActive = true;
        this.updateCheckboxes();
        return false;
      }

    },

    async updateCheckboxes() {
      setTimeout(() => {
        const checkedRows = this.queueStoreData.filter(item => this.filterQueues.includes(item.queueId));
        const grid = this.$refs.xGrid;
        if(grid) {
          this.$refs.xGrid.setCheckboxRow(checkedRows,checkedRows);
          this.widgetValue = this.getFilteredValue();
          this.longestCurrentHoldValue = this.getFilteredLongestHold();
        }
      }, 500)
    },

    getInitialValue() {
      setTimeout( () => {
        this.widgetValue = this.getFilteredValue();
        this.longestCurrentHoldValue = this.getFilteredLongestHold();
      }, 500)
    },

    //input is time in seconds. output is pretty time.
    timeFormatter(data) {
      const { cellValue: time } = data;
      const hours = Math.floor(time / 3600);
      const remainderSeconds = time % 3600;
      const minutes = Math.floor(remainderSeconds / 60);
      const seconds = Math.round(remainderSeconds % 60);

      if (hours > 0) {return hours + 'hr ' + minutes + 'm';}
      if (minutes > 0) {return minutes + 'm ' + seconds + 's';}
      if (seconds > 0) {return seconds + 's';}
      return '0';
    },

    // sorting function for hold time
    holdTimeSort(data) {
      return data.longestCurrentHold ? data.longestCurrentHold : 0;
    }
  },
}

</script>
